/**
 * Schedule Store — the Excel schedule-fill session.
 *
 * Flow: load an `excel_schedule` artifact → wait out ingest (poll) → read the
 * workbook model straight from S3 → review fill proposals → export a filled
 * .xlsx.
 *
 * Two things are load-bearing here:
 *   - the workbook model is fetched with plain `fetch` from a presigned URL, NOT
 *     through `apiRequest` — an Authorization header on an S3 GET is rejected.
 *   - every poll continuation is epoch-guarded (same pattern as
 *     `structure-proposal-store`), so switching artifacts or resetting can never
 *     be clobbered by an in-flight loop belonging to the previous artifact.
 */

import { create } from 'zustand'
import {
  createManualFill,
  getSchedule,
  getFillProposals,
  decideFillProposal,
  decideAllFillProposals,
  exportSchedule,
  getScheduleExports,
  reingestSchedule,
  type ScheduleInfo,
  type ScheduleExportResult,
} from '@/lib/api'
import type {
  FillDecision,
  FillProposal,
  FillProposalCounts,
  FillSource,
  ScheduleExportWithUrl,
  ScheduleIngestStatus,
  ScheduleMetadata,
  ScheduleOutline,
  WorkbookModel,
} from '@/types/schedule'

export const SCHEDULE_POLL_INTERVAL_MS = 3000
/** ~5 minutes. Ingest is seconds for a normal workbook; past this it is stuck. */
const MAX_POLL_ATTEMPTS = 100

const LOAD_ERROR = "Couldn't load the schedule — try again."
const MODEL_ERROR = "Couldn't open the workbook — try again."
const TIMEOUT_ERROR =
  'Preparing this workbook is taking longer than expected — try again in a moment.'

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function messageOf(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message : ''
  return message.length > 0 ? message : fallback
}

/**
 * Counts after a single decision, without a second round trip. Mirrors
 * `countsByArtifact` exactly: `accepted` is accepted-but-unwritten, and a fill
 * moves to `applied` once an export has carried it.
 */
function countProposals(proposals: FillProposal[]): FillProposalCounts {
  const counts: FillProposalCounts = { pending: 0, accepted: 0, dismissed: 0, applied: 0 }
  for (const proposal of proposals) {
    if (proposal.status === 'pending') counts.pending++
    else if (proposal.status === 'dismissed') counts.dismissed++
    else if (proposal.status === 'accepted') {
      if (proposal.appliedAt === null) counts.accepted++
      else counts.applied++
    }
  }
  return counts
}

interface ScheduleState {
  artifactId: string | null
  schedule: ScheduleMetadata | null
  /** Null until ingest is ready and the presigned model fetch succeeds. */
  model: WorkbookModel | null
  outline: ScheduleOutline | null
  ingestStatus: ScheduleIngestStatus | null
  proposals: FillProposal[]
  counts: FillProposalCounts | null
  activeSheet: string | null
  exports: ScheduleExportWithUrl[]
  isLoading: boolean
  isExporting: boolean
  error: string | null

  load: (artifactId: string) => Promise<void>
  refreshProposals: () => Promise<void>
  refreshExports: () => Promise<void>
  setActiveSheet: (sheet: string) => void
  /** Stage+accept a value the user typed into an untouched cell. Returns success. */
  createManualFill: (sheet: string, address: string, value: string) => Promise<boolean>
  decide: (proposalId: string, decision: FillDecision, editedValue?: string) => Promise<void>
  decideAll: (
    decision: FillDecision,
    filter?: { sheet?: string; source?: FillSource }
  ) => Promise<void>
  doExport: () => Promise<ScheduleExportResult | null>
  /** Re-run ingest for a schedule stuck on 'failed'/'pending', then resume polling. */
  reingest: () => Promise<void>
  reset: () => void
}

const initialState = {
  artifactId: null,
  schedule: null,
  model: null,
  outline: null,
  ingestStatus: null,
  proposals: [],
  counts: null,
  activeSheet: null,
  exports: [],
  isLoading: false,
  isExporting: false,
  error: null,
} satisfies Omit<
  ScheduleState,
  | 'load'
  | 'refreshProposals'
  | 'refreshExports'
  | 'setActiveSheet'
  | 'createManualFill'
  | 'decide'
  | 'decideAll'
  | 'doExport'
  | 'reingest'
  | 'reset'
>

/**
 * Bumped by every poll start and by reset(). A continuation whose epoch is stale
 * returns without writing, so a superseded artifact can't clobber the live one.
 */
let pollEpoch = 0

type Setter = (patch: Partial<ScheduleState>) => void
type Getter = () => ScheduleState

/** The schedule/outline/counts a GET response settles, independent of ingest state. */
function infoPatch(info: ScheduleInfo): Partial<ScheduleState> {
  return {
    schedule: info.schedule,
    outline: info.outline,
    ingestStatus: info.schedule.ingestStatus,
    counts: info.counts,
  }
}

function firstSheetName(model: WorkbookModel, outline: ScheduleOutline): string | null {
  return model.sheets[0]?.name ?? outline.sheets[0]?.name ?? null
}

/**
 * Fetch the workbook model from its presigned URL and load the proposals.
 *
 * The two are INDEPENDENT sources and are loaded independently: a transient S3
 * failure on the presigned model (403, expired signature, network blip) must not
 * take the proposals list down with it, or the review panel renders empty next to
 * a workbook-specific error. A model failure is reported but does not clear the
 * schedule either — the user still needs to see which workbook they are on, and
 * can retry.
 */
async function hydrateReady(
  artifactId: string,
  info: ScheduleInfo,
  epoch: number,
  set: Setter
): Promise<void> {
  let modelError: string | null = null

  if (info.modelUrl) {
    let model: WorkbookModel | null = null
    try {
      const response = await fetch(info.modelUrl)
      if (!response.ok) throw new Error(`Model fetch failed (${response.status})`)
      model = (await response.json()) as WorkbookModel
    } catch {
      modelError = MODEL_ERROR
    }
    if (epoch !== pollEpoch) return
    set(
      model
        ? { model, activeSheet: firstSheetName(model, info.outline) }
        : { model: null, error: modelError }
    )
  }

  let proposals: { proposals: FillProposal[]; counts: FillProposalCounts }
  try {
    proposals = await getFillProposals(artifactId)
  } catch (error) {
    if (epoch !== pollEpoch) return
    // The model error wins when both fail — without a grid there is nothing to
    // review, so that is the more actionable of the two.
    set({ error: modelError ?? messageOf(error, LOAD_ERROR) })
    return
  }
  if (epoch !== pollEpoch) return
  set({ proposals: proposals.proposals, counts: proposals.counts })
}

/** Settle a schedule GET: ready → hydrate, failed → surface, pending → keep polling. */
async function settle(
  artifactId: string,
  info: ScheduleInfo,
  epoch: number,
  set: Setter
): Promise<boolean> {
  set(infoPatch(info))

  if (info.schedule.ingestStatus === 'failed') {
    set({ error: info.schedule.ingestError ?? LOAD_ERROR })
    return true
  }
  if (info.schedule.ingestStatus === 'ready') {
    await hydrateReady(artifactId, info, epoch, set)
    return true
  }
  return false
}

/** Poll the schedule until ingest leaves 'pending'. */
async function pollUntilSettled(artifactId: string, epoch: number, set: Setter): Promise<void> {
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    await sleep(SCHEDULE_POLL_INTERVAL_MS)
    if (epoch !== pollEpoch) return

    const info = await getSchedule(artifactId).catch(() => null)
    if (epoch !== pollEpoch) return
    if (!info) continue

    if (await settle(artifactId, info, epoch, set)) return
    if (epoch !== pollEpoch) return
  }
  set({ error: TIMEOUT_ERROR })
}

async function startLoad(
  artifactId: string,
  set: Setter,
  patch: Partial<ScheduleState>
): Promise<void> {
  const epoch = ++pollEpoch
  set({ ...patch, artifactId, isLoading: true, error: null })

  let info: ScheduleInfo
  try {
    info = await getSchedule(artifactId)
  } catch (error) {
    if (epoch !== pollEpoch) return
    set({ isLoading: false, error: messageOf(error, LOAD_ERROR) })
    return
  }
  if (epoch !== pollEpoch) return

  const settled = await settle(artifactId, info, epoch, set)
  if (epoch !== pollEpoch) return
  set({ isLoading: false })
  if (!settled) await pollUntilSettled(artifactId, epoch, set)
}

export const useScheduleStore = create<ScheduleState>((set, get: Getter) => ({
  ...initialState,

  load: async (artifactId) => {
    // A different artifact must not inherit the previous workbook's grid.
    const isSwitch = get().artifactId !== artifactId
    await startLoad(
      artifactId,
      set,
      isSwitch
        ? { model: null, outline: null, schedule: null, proposals: [], counts: null, activeSheet: null, exports: [], ingestStatus: null }
        : {}
    )
  },

  refreshProposals: async () => {
    const { artifactId } = get()
    if (!artifactId) return
    try {
      const { proposals, counts } = await getFillProposals(artifactId)
      set({ proposals, counts })
    } catch (error) {
      set({ error: messageOf(error, LOAD_ERROR) })
    }
  },

  refreshExports: async () => {
    const { artifactId } = get()
    if (!artifactId) return
    try {
      const { exports } = await getScheduleExports(artifactId)
      set({ exports })
    } catch (error) {
      set({ error: messageOf(error, LOAD_ERROR) })
    }
  },

  setActiveSheet: (sheet) => set({ activeSheet: sheet }),

  // Deliberately NOT optimistic: `decide` is an atomic server-side claim that can
  // legitimately lose (already decided, superseded), and the row it returns is the
  // authority. Painting the decision first would show a state the server refused.
  createManualFill: async (sheet, address, value) => {
    const { artifactId } = get()
    if (!artifactId) return false
    try {
      const { proposal } = await createManualFill(artifactId, { sheet, address, value })
      // Server-accepted already; drop any superseded predecessor from view so the
      // grid paints the new value immediately rather than waiting on a refetch.
      const proposals = [
        ...get().proposals.filter((p) => p.targetKey !== proposal.targetKey),
        proposal,
      ]
      set({ proposals, counts: countProposals(proposals), error: null })
      return true
    } catch (error) {
      set({ error: messageOf(error, "Couldn't save that value — try again.") })
      return false
    }
  },

  decide: async (proposalId, decision, editedValue) => {
    const { artifactId } = get()
    if (!artifactId) return
    try {
      const { proposal } = await decideFillProposal(artifactId, proposalId, decision, editedValue)
      const proposals = get().proposals.map((p) => (p.id === proposal.id ? proposal : p))
      set({ proposals, counts: countProposals(proposals), error: null })
    } catch (error) {
      set({ error: messageOf(error, "Couldn't save that decision — try again.") })
    }
  },

  decideAll: async (decision, filter) => {
    const { artifactId } = get()
    if (!artifactId) return
    try {
      await decideAllFillProposals(artifactId, decision, filter)
    } catch (error) {
      set({ error: messageOf(error, "Couldn't save those decisions — try again.") })
      return
    }
    set({ error: null })
    await get().refreshProposals()
  },

  doExport: async () => {
    const { artifactId } = get()
    if (!artifactId) return null
    set({ isExporting: true, error: null })
    let result: ScheduleExportResult
    try {
      result = await exportSchedule(artifactId)
    } catch (error) {
      // 400 'No accepted fills to apply' lands here — a normal outcome the UI
      // reports, not a crash.
      set({ isExporting: false, error: messageOf(error, "Couldn't export the workbook — try again.") })
      return null
    }
    set({ isExporting: false })
    // The export stamps applied_at on the fills it wrote, so both lists moved.
    await get().refreshProposals()
    await get().refreshExports()
    return result
  },

  reingest: async () => {
    const { artifactId } = get()
    if (!artifactId) return
    try {
      await reingestSchedule(artifactId)
    } catch (error) {
      set({ error: messageOf(error, "Couldn't retry preparing this workbook — try again.") })
      return
    }
    // The artifact is pending again server-side; re-enter the poll loop.
    await startLoad(artifactId, set, { ingestStatus: 'pending', model: null })
  },

  reset: () => {
    // Orphan any in-flight poll loop so it can't write into the cleared store.
    pollEpoch++
    set({ ...initialState })
  },
}))
